import * as React from 'react';
import { Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, Dropdown, MessageBar, MessageBarBody, Option, Spinner, Textarea } from '@fluentui/react-components';
import { AttachRegular, DocumentRegular } from '@fluentui/react-icons';
import { getProgram, STAGES, type IProgram, type StageId } from '../../api/src/programs';
import { errorText, useApp } from '../app/context';
import { type IItem, listChildren } from '../graphx';
import { applicationView, type IApplicationView, type IAttached, type IClientView, type IItemView, itemView, localToday, type IPersonView, outstandingCount, personView, stageLabel } from '../records/model';
import { eventView, type IEventView, nextDue } from '../records/events';
import { DeadlinesTab, RequestsTab } from './ApplicationTabs';
import type { RecordSet } from '../records/FirmRecords';
import { ClosingTab, SubmissionTab } from './ApplicationClosing';
import { ITEM_STATUSES } from '../records/schemas';
import { addInstalments } from '../records/stage';
import { useLoad } from '../records/useLoad';
import { EmailUploadLinkButton } from '../components/UploadLinkCard';
import { shortDate } from './Home';

const TABS: { id: string; label: string }[] = [
  { id: 'checklist', label: 'Checklist' },
  { id: 'requests', label: 'Requests' },
  { id: 'deadlines', label: 'Deadlines' },
  { id: 'submission', label: 'Submission' },
  { id: 'closing', label: 'Decision and closing' }
];

interface ILoaded {
  set: RecordSet;
  application: IApplicationView;
  clientName: string;
  client?: IClientView;
  people: IPersonView[];
  items: IItemView[];
  events: IEventView[];
}

const TO_CHECK: string = 'Uploaded by client (check)';

/** One application: who and what, its stage, the one next step, and its tabs (the checklist first). */
export default function ApplicationPage(props: { applicationKey: string; tab?: string; show?: string }): React.ReactElement {
  const app = useApp();
  const { data, error, reload } = useLoad<ILoaded | undefined>(async () => {
    const found = await app.records.find(props.applicationKey);
    if (!found) {
      return undefined;
    }
    const { set } = found;
    const application: IApplicationView = applicationView(found.record);
    const [client, people, items, events] = await Promise.all([
      app.records.findClient(application.clientKey),
      set.people.query({ ApplicationKey: application.key }),
      set.checklist.query({ ApplicationKey: application.key }),
      set.events.query({ ApplicationKey: application.key })
    ]);
    const c: IClientView | undefined = client;
    return { set, application, clientName: c?.name ?? '', client: c, people: people.map(personView), items: items.map(itemView), events: events.map(eventView) };
  }, [app.records, props.applicationKey]);
  const [problem, setProblem] = React.useState<string | undefined>(undefined);

  // Restricted files: who opened which file is kept in their own history.
  const logged = React.useRef<string>('');
  React.useEffect(() => {
    if (data && data.set.restricted && logged.current !== data.application.key) {
      logged.current = data.application.key;
      data.set.writer.batch().viewed(data.set.applications, data.application.rec, data.application.title, `${app.account.name || app.account.username} opened ${data.application.title}`)
        .catch(e => console.warn('Could not log the opening of a restricted file', errorText(e)));
    }
  }, [data, app.account]);

  // Keep the summaries on the board and Today right (they're summaries, so not logged in History).
  React.useEffect(() => {
    if (data) {
      const n: number = outstandingCount(data.items);
      const due: string | undefined = nextDue(data.events);
      if (n !== data.application.outstanding || (due ?? '') !== (data.application.nextDue ?? '')) {
        data.set.applications.update(data.application.rec.id, { Outstanding: n, NextDue: due }).catch(() => undefined);
      }
    }
  }, [data, app.records]);

  if (error) {
    return <main className="page"><MessageBar intent="error"><MessageBarBody>{error}</MessageBarBody></MessageBar></main>;
  }
  if (data === undefined) {
    return <main className="page"><Spinner size="small" label="Loading the application…" labelPosition="after" /></main>;
  }
  if (!data) {
    return <main className="page"><div className="card">This application isn't in your records. <a href="#/applications">Back to applications</a></div></main>;
  }

  const a: IApplicationView = data.application;
  const program: IProgram | undefined = getProgram(a.programId);
  const tab: string = TABS.some(t => t.id === props.tab) ? props.tab! : 'checklist';
  const base: string = `#/applications/${encodeURIComponent(a.key)}`;
  const stages = STAGES.filter(s => s.id !== 'closed' && (s.id !== 'additional' || a.stage === 'additional'));
  const at: number = stages.findIndex(s => s.id === a.stage);

  const moveTo = async (stage: StageId): Promise<void> => {
    setProblem(undefined);
    try {
      const batch = data.set.writer.batch();
      await batch.update(data.set.applications, a.rec, { Stage: stageLabel(stage), Open: stage !== 'closed' }, a.title, `Moved ${a.title} to ${stageLabel(stage)}`);
      await addInstalments(data.set, batch, a, stage, app.workspace.firm?.instalments, data.events);
      reload();
    } catch (e) {
      setProblem(errorText(e));
    }
  };

  return (
    <main className="page" style={{ maxWidth: 1064 }}>
      <a href="#/applications" style={{ fontSize: 14 }}>← Applications</a>
      <div className="page-head" style={{ alignItems: 'flex-start' }}>
        <div className="stack">
          <h1>{data.clientName || a.title}{data.set.restricted && <span className="tag-warn" style={{ verticalAlign: 'middle' }}>Restricted</span>}</h1>
          <span className="muted" style={{ fontSize: 15 }}>
            {program?.name ?? a.programId}
            {data.client?.number && <> · <span className="mono" style={{ fontSize: 14 }}>{data.client.number}</span></>}
            {[a.lawyer, a.clerk].filter(Boolean).length > 0 && ` · ${[a.lawyer, a.clerk].filter(Boolean).join(', ')}`}
          </span>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {a.folderUrl && <Button size="small" as="a" href={a.folderUrl} target="_blank" rel="noreferrer">Folder</Button>}
          <Button size="small" as="a" href={`#/clients/${encodeURIComponent(a.clientKey)}`}>Client</Button>
          <Dropdown size="small" aria-label="Move to stage" value={`Stage: ${stageLabel(a.stage)}`} selectedOptions={[a.stage]} onOptionSelect={(_, d) => { if (d.optionValue && d.optionValue !== a.stage) { void moveTo(d.optionValue as StageId); } }} style={{ minWidth: 0 }}>
            {STAGES.map(s => <Option key={s.id} value={s.id}>{s.label}</Option>)}
          </Dropdown>
        </div>
      </div>
      {a.stage !== 'closed' && (
        <ol className="stages" aria-label={`Stage: ${stageLabel(a.stage)}`}>
          {stages.map((s, i) => <li key={s.id} className={i === at ? 'now' : i < at ? 'done' : undefined} aria-current={i === at ? 'step' : undefined}>{s.label}</li>)}
        </ol>
      )}
      {problem && <MessageBar intent="error"><MessageBarBody>{problem}</MessageBarBody></MessageBar>}
      <NextStep data={{ ...data, program }} base={base} onMove={stage => { void moveTo(stage); }} onChanged={reload} />
      <nav className="tabs" aria-label="Application sections">
        {TABS.map(t => <a key={t.id} href={`${base}/${t.id}`} aria-current={tab === t.id ? 'page' : undefined}>{t.label}</a>)}
      </nav>
      {tab === 'checklist' && <Checklist data={data} program={program} show={props.show} base={base} onChanged={reload} />}
      {tab === 'requests' && <RequestsTab data={{ ...data, program }} onChanged={reload} />}
      {tab === 'deadlines' && <DeadlinesTab data={{ ...data, program }} onChanged={reload} />}
      {tab === 'submission' && <SubmissionTab data={{ ...data, program }} onChanged={reload} />}
      {tab === 'closing' && <ClosingTab data={{ ...data, program }} onChanged={reload} />}
    </main>
  );
}

/** The one thing to do next on this file, with one solid button. */
function NextStep(props: { data: ILoaded & { program?: IProgram }; base: string; onMove: (s: StageId) => void; onChanged: () => void }): React.ReactElement | null {
  const d = props.data;
  const a: IApplicationView = d.application;
  const today: string = localToday();
  const unchecked: IEventView | undefined = d.events.filter(e => e.kind === 'deadline' && !e.doneOn && !e.confirmedOn && !!e.due).sort((x, y) => x.due!.localeCompare(y.due!))[0];
  const toCheck: number = d.items.filter(i => i.status === TO_CHECK).length;
  const needed: IItemView[] = d.items.filter(i => i.status === 'Needed' || i.status === 'Requested');
  const notAsked: number = needed.filter(i => i.status === 'Needed').length;
  const signed: string[] = d.events.filter(e => e.kind === 'signoff' && !!e.doneOn).map(e => e.title);

  const box = (title: string, detail: string, action: React.ReactNode, urgent?: boolean): React.ReactElement => (
    <section className="next-step" aria-label="Next step">
      <div className="stack">
        <span className={urgent ? 'why' : 'eyebrow-sm'}>{urgent ? 'Needs you' : 'Next step'}</span>
        <strong>{title}</strong>
        {detail && <span className="small" style={{ fontSize: 14 }}>{detail}</span>}
      </div>
      {action}
    </section>
  );
  const go = (label: string, href: string): React.ReactElement => <Button appearance="primary" as="a" href={href}>{label}</Button>;

  if (a.stage === 'closed') {
    return null;
  }
  if (unchecked) {
    return box(`Check the date: ${unchecked.title}`, `Calculated as ${shortDate(unchecked.due!)}${unchecked.due! < today ? ', which has passed' : ''}. Check it against the letter and confirm it.`, go('Check the date', `${props.base}/deadlines`), true);
  }
  if (toCheck) {
    return box(`${toCheck} document${toCheck === 1 ? '' : 's'} the client sent to check`, 'Open each one and mark it received if it\'s what was asked for.', go('Check them', `${props.base}/checklist?show=check`));
  }
  if ((a.stage === 'opening' || a.stage === 'collecting' || a.stage === 'additional') && needed.length) {
    return box(`${needed.length} document${needed.length === 1 ? '' : 's'} still to collect`, notAsked ? `${notAsked} not asked for yet.` : 'All of them asked for.',
      <EmailUploadLinkButton data={d} primary onChanged={props.onChanged} />);
  }
  if (a.stage === 'opening' || a.stage === 'collecting') {
    return box('Everything on the checklist is in', 'Move the file to Review for the sign-offs.', <Button appearance="primary" onClick={() => props.onMove('review')}>Move to Review</Button>);
  }
  if (a.stage === 'review') {
    return signed.indexOf('Lawyer sign-off') === -1 || signed.indexOf('Clerk sign-off') === -1
      ? box('Sign off before submission', signed.length ? `${signed.join(' and ')} done.` : 'A clerk and a lawyer sign off.', go('Review', `${props.base}/submission`), true)
      : box('Both sign-offs are done', 'Record the submission once it\'s sent to IRCC.', go('Record the submission', `${props.base}/submission`));
  }
  if (a.stage === 'submitted' || a.stage === 'waiting') {
    return box('Waiting on IRCC', 'When a letter comes, record it here; the dates that follow are calculated for you to check.', go('Record a letter or event', `${props.base}/deadlines`));
  }
  if (a.stage === 'decision' || a.stage === 'closing') {
    return box(a.outcome ? 'Close the file' : 'Record the decision', a.outcome ? `${a.outcome}.` : 'What IRCC decided, and the follow-ups that come with it.', go(a.outcome ? 'Closing steps' : 'Record the decision', `${props.base}/closing`));
  }
  return null;
}

function Checklist(props: { data: ILoaded; program?: IProgram; show?: string; base: string; onChanged: () => void }): React.ReactElement {
  const { people, items, application, events } = props.data;
  const [show, setShow] = React.useState<'needed' | 'check' | 'all'>(props.show === 'check' || props.show === 'all' ? props.show : 'needed');
  React.useEffect(() => { if (props.show === 'check' || props.show === 'all') { setShow(props.show); } }, [props.show]);
  const toCheck: IItemView[] = items.filter(i => i.status === TO_CHECK);
  const needed: IItemView[] = items.filter(i => i.status === 'Needed' || i.status === 'Requested');
  const listed: IItemView[] = show === 'all' ? items.filter(i => i.status !== TO_CHECK) : show === 'needed' ? needed : [];
  // Grouped by person (in the order they were added), then the file's own items.
  const groups: { title: string; items: IItemView[] }[] = [
    ...people.map(p => ({ title: `${p.name}${p.role ? ` · ${p.role.toLowerCase()}` : ''}`, items: listed.filter(i => i.personKey === p.key) })),
    { title: 'For the whole file', items: listed.filter(i => !i.personKey || !people.some(p => p.key === i.personKey)) }
  ].filter(g => g.items.length > 0);
  const deadlines: IEventView[] = events.filter(e => (e.kind === 'deadline' || e.kind === 'instalment') && !e.doneOn && !!e.due).sort((x, y) => x.due!.localeCompare(y.due!));
  const p = props.program;

  return (
    <div className="split">
      <section className="main" aria-label="Checklist">
        <div className="spread" style={{ alignItems: 'center' }}>
          <div className="seg" role="group" aria-label="Show">
            <button type="button" aria-pressed={show === 'needed'} onClick={() => setShow('needed')}>Still needed ({needed.length})</button>
            <button type="button" aria-pressed={show === 'check'} onClick={() => setShow('check')}>To check ({toCheck.length})</button>
            <button type="button" aria-pressed={show === 'all'} onClick={() => setShow('all')}>All ({items.length})</button>
          </div>
          {p && (
            <span className="small">
              {p.review === 'pending' ? 'Checklist pending lawyer review · ' : ''}IRCC pages as of {application.programVersion}
              {p.version !== application.programVersion ? ' · a newer version exists' : ''}
            </span>
          )}
        </div>
        {(show === 'check' || show === 'needed') && toCheck.length > 0 && (
          <div className="card" style={{ gap: 0, padding: '6px 20px 8px' }}>
            <h2 style={{ margin: '10px 0 4px', fontSize: 15 }}>To check · sent by the client</h2>
            {toCheck.map(i => <ItemRow key={i.key} item={i} application={application} set={props.data.set} onChanged={props.onChanged} />)}
          </div>
        )}
        {show === 'check' && toCheck.length === 0 && <div className="card"><span className="muted">Nothing the client sent is waiting to be checked.</span></div>}
        {show === 'needed' && needed.length === 0 && <div className="card"><span className="muted">Nothing is still needed. <button type="button" className="link-btn" style={{ color: 'var(--blue)' }} onClick={() => setShow('all')}>See all {items.length}</button></span></div>}
        {groups.length > 0 && (
          <div className="card" style={{ gap: 0, padding: '6px 20px 8px' }}>
            {groups.map((g, n) => (
              <React.Fragment key={g.title}>
                <h2 style={{ margin: `${n ? 16 : 10}px 0 4px`, fontSize: 15 }}>{g.title}</h2>
                {g.items.map(i => <ItemRow key={i.key} item={i} application={application} set={props.data.set} onChanged={props.onChanged} />)}
              </React.Fragment>
            ))}
          </div>
        )}
      </section>
      <aside aria-label="Deadlines">
        <h2 className="h2" style={{ fontSize: 15 }}>Deadlines</h2>
        {deadlines.length === 0 && <span className="small">None yet. They're added when you record a letter.</span>}
        {deadlines.slice(0, 5).map(e => (
          <div key={e.key} className="card" style={{ gap: 4, padding: '14px 18px' }}>
            <strong style={{ fontSize: 15 }}>{e.title}</strong>
            <span style={{ fontSize: 14 }}>
              {shortDate(e.due!)} · {e.kind === 'instalment' ? 'fee instalment' : e.confirmedOn ? `checked by ${e.confirmedBy}` : <span className="needs-you">calculated, please check</span>}
            </span>
            {e.kind === 'deadline' && !e.confirmedOn && <a href={`${props.base}/deadlines`} style={{ fontSize: 14 }}>Check the date</a>}
          </div>
        ))}
        <Button as="a" href={`${props.base}/deadlines`} style={{ width: '100%' }}>Record a letter or event</Button>
        <p className="small" style={{ margin: '4px 0 0', lineHeight: 1.5 }}>Dates are calculated for you to check against the letter. Your representative is responsible for every deadline.</p>
      </aside>
    </div>
  );
}

/** What an item's status says on its row, in plain grey (only "to check" is marked). */
function statusWords(i: IItemView): string {
  const when: string = i.changedOn ? ` ${new Date(i.changedOn).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })}` : '';
  switch (i.status) {
    case 'Needed': return 'Not requested';
    case 'Requested': return `Requested${when}`;
    case 'Received': return `Received${when}`;
    default: return i.status;
  }
}

function ItemRow(props: { item: IItemView; application: IApplicationView; set: RecordSet; onChanged: () => void }): React.ReactElement {
  const app = useApp();
  const i: IItemView = props.item;
  const [working, setWorking] = React.useState<boolean>(false);
  const [problem, setProblem] = React.useState<string | undefined>(undefined);
  const [open, setOpen] = React.useState<boolean>(false);
  const [editing, setEditing] = React.useState<boolean>(false);
  const [note, setNote] = React.useState<string>(i.note ?? '');
  const [picking, setPicking] = React.useState<boolean>(false);
  const check: boolean = i.status === TO_CHECK;

  const change = async (fields: { [c: string]: string | undefined }, reason: string): Promise<void> => {
    setWorking(true);
    setProblem(undefined);
    try {
      await props.set.writer.batch().update(props.set.checklist, i.rec, { ...fields, ChangedBy: app.account.username, ChangedOn: new Date().toISOString() }, i.label, reason);
      props.onChanged();
    } catch (e) {
      setProblem(errorText(e));
    } finally {
      setWorking(false);
    }
  };

  const attach = async (file: IItem): Promise<void> => {
    setPicking(false);
    const docs: IAttached[] = [...i.documents.filter(d => d.id !== file.id), { id: file.id, name: file.name, url: file.webUrl ?? '' }];
    await change({ Documents: JSON.stringify(docs) }, `Attached "${file.name}" to "${i.label}"`);
  };

  return (
    <div className="item-row">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
        {check && <span className="flag" aria-hidden="true" />}
        <div className="stack">
          <span style={{ fontSize: 15 }}>{i.label}{i.form ? <span className="muted"> · {i.form}</span> : null}</span>
          {(open || check) && i.documents.length > 0 && (
            <span className="small" style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {i.documents.map(d => <a key={d.id} href={d.url} target="_blank" rel="noreferrer"><DocumentRegular /> {d.name}</a>)}
            </span>
          )}
          {open && <span className="small">{i.section}{i.changedBy ? ` · ${i.status} by ${i.changedBy}` : ''}</span>}
          {i.note && !editing && <span className="small" style={{ whiteSpace: 'pre-wrap' }}>Note: {i.note}</span>}
          {editing && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <Textarea value={note} onChange={(_, d) => setNote(d.value)} />
              <div style={{ display: 'flex', gap: 8 }}>
                <Button size="small" appearance="primary" disabled={working} onClick={() => { setEditing(false); void change({ Note: note.trim() || undefined }, `Changed the note on "${i.label}"`); }}>Save note</Button>
                <Button size="small" onClick={() => { setEditing(false); setNote(i.note ?? ''); }}>Cancel</Button>
              </div>
            </div>
          )}
          {open && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 4 }}>
              <Dropdown size="small" aria-label={`Status of ${i.label}`} disabled={working} value={i.status} selectedOptions={[i.status]} style={{ minWidth: 0 }}
                onOptionSelect={(_, d) => { if (d.optionValue && d.optionValue !== i.status) { void change({ Status: d.optionValue }, `Marked "${i.label}" ${d.optionValue.toLowerCase()}`); } }}>
                {ITEM_STATUSES.map(s => <Option key={s} value={s}>{s}</Option>)}
              </Dropdown>
              <Button size="small" icon={<AttachRegular />} disabled={working || !props.application.folderId} onClick={() => setPicking(true)}>Attach</Button>
              <Button size="small" disabled={working} onClick={() => setEditing(true)}>Note</Button>
            </div>
          )}
          {problem && <span className="small" style={{ color: 'var(--red)' }}>{problem}</span>}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
        {check
          ? <Button size="small" disabled={working} onClick={() => { void change({ Status: 'Received' }, `Checked "${i.label}" and marked it received`); }}>Mark received</Button>
          : <span className="status">{statusWords(i)}</span>}
        <Button size="small" appearance="subtle" aria-expanded={open} aria-label={`${open ? 'Close' : 'Change'} ${i.label}`} onClick={() => setOpen(!open)}>{open ? 'Close' : 'Change'}</Button>
      </div>
      {picking && props.application.folderId && <FilePicker driveId={props.set.library.driveId} folderId={props.application.folderId} onPick={f => { void attach(f); }} onClose={() => setPicking(false)} />}
    </div>
  );
}

/** Files in the application's folder (and its person folders), to attach to an item. */
function FilePicker(props: { driveId: string; folderId: string; onPick: (f: IItem) => void; onClose: () => void }): React.ReactElement {
  const app = useApp();
  const { data, error } = useLoad(async () => {
    const out: { path: string; item: IItem }[] = [];
    const walk = async (id: string, path: string, depth: number): Promise<void> => {
      for (const c of await listChildren(app.graph, props.driveId, id, 500)) {
        if (c.isFolder) {
          if (depth < 2) {
            await walk(c.id, path ? `${path} › ${c.name}` : c.name, depth + 1);
          }
        } else {
          out.push({ path, item: c });
        }
      }
    };
    await walk(props.folderId, '', 0);
    return out;
  }, [props.driveId, props.folderId]);
  return (
    <Dialog open onOpenChange={(_, d) => { if (!d.open) { props.onClose(); } }}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Attach a document</DialogTitle>
          <DialogContent style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 420, overflowY: 'auto' }}>
            {error && <span style={{ color: 'var(--red)' }}>{error}</span>}
            {!data && !error && <Spinner size="tiny" label="Looking in the application's folder…" labelPosition="after" />}
            {data && data.length === 0 && <span className="small">No documents in the application's folder yet. Put them there first (open the folder from the top of this page).</span>}
            {data && data.map(f => (
              <Button key={f.item.id} appearance="subtle" icon={<DocumentRegular />} onClick={() => props.onPick(f.item)} style={{ justifyContent: 'flex-start' }}>
                {f.item.name}{f.path ? <span className="small" style={{ marginLeft: 6 }}>({f.path})</span> : null}
              </Button>
            ))}
          </DialogContent>
          <DialogActions><Button onClick={props.onClose}>Close</Button></DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
